import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import { DIVISION_LABELS, formatCityState } from '@tnf-recruit/shared'
import type { Division } from '@tnf-recruit/shared'

/** Public school directory row — no auth, so only non-sensitive columns. */
export interface PublicSchool {
  id: string
  name: string
  division: Division
  city: string
  state: string
  logoUrl?: string | null
  emailDomains?: string[]
}

/** Debounce so a typing recruiter doesn't spend the API rate limit per keystroke. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

/**
 * Search the curated school list. Unauthenticated on purpose — the recruiter
 * picks their school on the signup page, before an account exists.
 */
export function useSchoolDirectory(query: string, enabled = true) {
  const debouncedQuery = useDebounced(query)

  return useQuery({
    queryKey: ['schoolDirectory', debouncedQuery],
    enabled,
    queryFn: async (): Promise<PublicSchool[]> => {
      const { data } = await apiClient.get('/schools', {
        params: { query: debouncedQuery || undefined, limit: 25 },
      })
      return (data.data ?? []) as PublicSchool[]
    },
  })
}

export function describeSchool(school: PublicSchool): string {
  return [
    DIVISION_LABELS[school.division] ?? school.division,
    formatCityState(school.city, school.state),
  ]
    .filter(Boolean)
    .join(' · ')
}
